"use client";

import { faSearch, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { SquareButton, Typography } from "@tosize/ui";
import { useCallback } from "react";
import { useRouter } from "~/hooks/useRouter";
import type { DropProps } from "../Drop";
import { Suggestions } from "./MiniSearch";
import SearchResult from "./Result";
import useSearch from "./useSearch";

interface Props {
	lang: string;
	suggestions: { title: string; href: string }[];
	onOpenChange: DropProps["closeAction"];
	messages: {
		title: string;
		inputTitle: string;
		closeTitle: string;
		suggestionsTitle: string;
		loading: string;
		noResults: string;
	};
}

export default function DropSearch(props: Props) {
	const { suggestions = [], onOpenChange } = props;

	const router = useRouter();

	const { resetState, searchTextHandler, localData, searchText, loading } = useSearch(props.lang);

	const closeMenuHandler = useCallback(() => {
		onOpenChange();
		resetState();
	}, [onOpenChange, resetState]);

	return (
		<>
			<form
				className="container flex items-center py-4 text-2xl"
				onSubmit={async (e) => {
					e.preventDefault();

					if (searchText) {
						router.push(`/search?q=${encodeURIComponent(searchText)}`);

						onOpenChange();
					}
				}}
			>
				<SquareButton
					type="submit"
					component="button"
					title={props.messages.title}
					icon={faSearch}
					color="shades"
					className="mr-2"
				/>

				<input
					// biome-ignore lint/a11y/noAutofocus: it should be focused when opened
					autoFocus
					type="search"
					placeholder={`${props.messages.inputTitle}...`}
					value={searchText}
					className="h-10 w-full appearance-none rounded border border-shades-light bg-white p-2 text-base leading-none hover:border-primary-medium focus:outline-none focus-visible:border-primary-medium focus-visible:ring-1 focus-visible:ring-primary-medium motion-safe:transition-colors"
					onChange={(e) => searchTextHandler(e.target.value)}
				/>
				<SquareButton
					component="button"
					title={props.messages.closeTitle}
					icon={faTimes}
					onClick={() => closeMenuHandler()}
					color="shades"
					className="ml-2"
				/>
			</form>

			<div className="max-h-[calc(var(--window-y,100dvh)-var(--header-y)-var(--search-y))] overflow-auto bg-white pt-4 pb-2 [--search-y:72px]">
				{searchText ? (
					<SearchResult
						lang={props.lang}
						loading={!localData && loading}
						data={localData}
						searchText={searchText}
						messages={{
							loading: props.messages.loading,
							noResults: props.messages.noResults,
						}}
						className="container divide-y divide-shades sm:px-[64px]"
					/>
				) : (
					<div className="container sm:px-[64px]">
						<Typography
							variant="paragraph-italic"
							color="text-shades-dark"
							size="text-sm"
							className="truncate px-1 py-1 text-left"
						>
							{props.messages.suggestionsTitle}
						</Typography>
						{suggestions.map((suggestion, index) => (
							<Suggestions key={index} url={suggestion.href} title={suggestion.title} />
						))}
					</div>
				)}
			</div>
		</>
	);
}
